// Unidades soportadas para la prueba de prestaciones de grupos de presión.
// Conversiones a unidad canónica (l/min para caudal, bar para presión).

export type UnidadCaudal = 'm3h' | 'lpm' | 'lps' | 'gpm';
export type UnidadPresion = 'bar' | 'psi' | 'mca' | 'kgcm2' | 'kpa' | 'mpa';

export const UNIDADES_CAUDAL: { value: UnidadCaudal; label: string }[] = [
  { value: 'lpm', label: 'l/min' },
  { value: 'm3h', label: 'm³/h' },
  { value: 'lps', label: 'l/s' },
  { value: 'gpm', label: 'gpm (US)' },
];

export const UNIDADES_PRESION: { value: UnidadPresion; label: string }[] = [
  { value: 'bar', label: 'bar' },
  { value: 'psi', label: 'psi' },
  { value: 'mca', label: 'mca (m)' },
  { value: 'kgcm2', label: 'kg/cm²' },
  { value: 'kpa', label: 'kPa' },
  { value: 'mpa', label: 'MPa' },
];

export const labelCaudal = (u: UnidadCaudal): string =>
  UNIDADES_CAUDAL.find(x => x.value === u)?.label || 'l/min';

export const labelPresion = (u: UnidadPresion): string =>
  UNIDADES_PRESION.find(x => x.value === u)?.label || 'bar';

/** Parsea la etiqueta visible (la que se almacena en `datos_tecnicos`) a código de unidad. */
export const parseUnidadCaudal = (s?: string | null): UnidadCaudal => {
  if (!s) return 'lpm';
  const n = s.toLowerCase().replace(/\s+/g, '');
  if (n.includes('m3') || n.includes('m³')) return 'm3h';
  if (n === 'l/s' || n === 'lps') return 'lps';
  if (n.includes('gpm')) return 'gpm';
  return 'lpm';
};

export const parseUnidadPresion = (s?: string | null): UnidadPresion => {
  if (!s) return 'bar';
  const n = s.toLowerCase().replace(/\s+/g, '');
  if (n === 'psi') return 'psi';
  if (n === 'mca' || n === 'm') return 'mca';
  if (n.includes('kg')) return 'kgcm2';
  if (n === 'kpa') return 'kpa';
  if (n === 'mpa') return 'mpa';
  return 'bar';
};

// Factores hacia la unidad canónica (l/min y bar).
// caudal canónico: l/min
const FACTOR_CAUDAL_A_LPM: Record<UnidadCaudal, number> = {
  lpm: 1,
  m3h: 1000 / 60,     // 1 m³/h = 16.6667 l/min
  lps: 60,            // 1 l/s = 60 l/min
  gpm: 3.785411784,   // 1 US gpm = 3.7854 l/min
};

// presión canónica: bar
const FACTOR_PRESION_A_BAR: Record<UnidadPresion, number> = {
  bar: 1,
  psi: 0.0689475729,
  mca: 0.0980665,     // 1 m de columna de agua = 0.0981 bar
  kgcm2: 0.980665,
  kpa: 0.01,
  mpa: 10,
};

export const caudalToCanonical = (v: number, u: UnidadCaudal): number =>
  v * FACTOR_CAUDAL_A_LPM[u];
export const caudalFromCanonical = (v: number, u: UnidadCaudal): number =>
  v / FACTOR_CAUDAL_A_LPM[u];

export const presionToCanonical = (v: number, u: UnidadPresion): number =>
  v * FACTOR_PRESION_A_BAR[u];
export const presionFromCanonical = (v: number, u: UnidadPresion): number =>
  v / FACTOR_PRESION_A_BAR[u];

export const convertCaudal = (v: number, from: UnidadCaudal, to: UnidadCaudal): number =>
  from === to ? v : caudalFromCanonical(caudalToCanonical(v, from), to);

export const convertPresion = (v: number, from: UnidadPresion, to: UnidadPresion): number =>
  from === to ? v : presionFromCanonical(presionToCanonical(v, from), to);

// Redondeo razonable según magnitud (cifras significativas legibles).
export const roundForUnit = (v: number): number => {
  if (!isFinite(v)) return 0;
  const abs = Math.abs(v);
  if (abs >= 100) return Math.round(v);
  if (abs >= 10) return Math.round(v * 10) / 10;
  return Math.round(v * 100) / 100;
};
